import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

const DEMO_STORAGE_KEY = "demo_user_pins"

export default class extends Controller {
    static values = {
        pinned: Boolean,
        pinId: String,
        pinableType: String,
        pinableId: String,
        metadata: Object,
        demo: Boolean,
        createUrl: String
    }

    static targets = ["icon", "label"]

    toggle(e) {
        e.preventDefault()
        e.stopPropagation()

        if (this.demoValue) {
            this._toggleDemo()
            return
        }

        this.element.disabled = true
        let request = this.pinnedValue ? this._unpinRequest() : this._pinRequest()
        request
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not update pin")
                this.pinnedValue = !!data.pinned
                this.pinIdValue = data.pin_id ? String(data.pin_id) : ""
                this._syncIcon()
            })
            .catch(() => {})
            .finally(() => {
                this.element.disabled = false
            })
    }

    pinnedValueChanged() {
        this._syncIcon()
        this._syncLabel()
        this.element.classList.toggle("is-pinned", this.pinnedValue)
        this.element.setAttribute("aria-label", this.pinnedValue ? "Unpin" : "Pin")
        // A labelled toggle (menu row) says it in the row itself — a tooltip
        // repeating the same word on top of visible text is just noise.
        if (!this.hasLabelTarget) this.element.setAttribute("title", this.pinnedValue ? "Unpin" : "Pin")
    }

    _pinRequest() {
        return fetch(this.createUrlValue, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "X-CSRF-Token": csrfToken()
            },
            credentials: "same-origin",
            body: JSON.stringify({
                pinable_type: this.pinableTypeValue,
                pinable_id: this.pinableIdValue,
                metadata: this.metadataValue || {}
            })
        }).then((response) => response.json().then((data) => ({ response, data })))
    }

    _unpinRequest() {
        let url = `${this.createUrlValue}/${encodeURIComponent(this.pinIdValue)}`
        return fetch(url, {
            method: "DELETE",
            headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        }).then((response) => response.json().then((data) => ({ response, data })))
    }

    _toggleDemo() {
        let key = `${this.pinableTypeValue}:${this.pinableIdValue}`
        let state = this._loadDemoState()
        if (state[key]) {
            delete state[key]
            this.pinnedValue = false
            this.pinIdValue = ""
        } else {
            state[key] = { metadata: this.metadataValue || {} }
            this.pinnedValue = true
            this.pinIdValue = key
        }
        this._saveDemoState(state)
    }

    _syncIcon() {
        if (!this.hasIconTarget) return
        this.iconTarget.className = this.pinnedValue ? "bi bi-pin-fill" : "bi bi-pin"
    }

    _syncLabel() {
        if (!this.hasLabelTarget) return
        const el = this.labelTarget
        const unpinned = el.dataset.label || "Pin"
        el.textContent = this.pinnedValue ? (el.dataset.pinnedLabel || unpinned) : unpinned
    }

    _loadDemoState() {
        try {
            let raw = sessionStorage.getItem(DEMO_STORAGE_KEY)
            return raw ? JSON.parse(raw) : {}
        } catch {
            return {}
        }
    }

    _saveDemoState(state) {
        try {
            sessionStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(state))
        } catch {
            // sessionStorage unavailable — ignore
        }
    }

}
